import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';
import { useParams, useNavigate } from 'react-router-dom';
import { getRestaurant } from '../../services/menuService';
import { FiDownload, FiArrowLeft } from 'react-icons/fi';
import Logo from '../../components/Logo';
import styles from './QRCodeGenerator.module.css';

export default function QRCodeGenerator() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [qrUrl, setQrUrl] = useState('');
  const [restaurant, setRestaurant] = useState(null);
  const [tableNumber, setTableNumber] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Add state for color customization
  const [fgColor, setFgColor] = useState('#000000'); // Default: black
  const [bgColor, setBgColor] = useState('#ffffff'); // Default: white
  
  useEffect(() => {
    const loadRestaurant = async () => {
      try {
        setLoading(true);
        const data = await getRestaurant(restaurantId);
        setRestaurant(data);
      } catch (error) {
        console.error('Error loading restaurant:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadRestaurant();
  }, [restaurantId]);
  
  useEffect(() => {
    if (restaurant) {
      generateQRCode();
    }
  }, [restaurant, tableNumber, fgColor, bgColor]); // Re-generate when colors change
  
  const generateQRCode = async () => {
    try {
      const deployedUrl = "https://qrbutlernew.onrender.com";
      
      // Construct the full menu URL with restaurant ID and optional table parameter
      const menuUrl = `${deployedUrl}/menu/${restaurantId}${tableNumber ? `?table=${tableNumber}` : ''}`;
      
      // Add debug logging
      console.log('Generated QR URL:', menuUrl);
      
      const qrCodeDataUrl = await QRCode.toDataURL(menuUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: fgColor, // Use customizable foreground color
          light: bgColor // Use customizable background color
        }
      });
      
      setQrUrl(qrCodeDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };
  
  const downloadQRCode = () => {
    if (qrUrl) {
      const fileName = `menu-qr-${restaurant.name}${tableNumber ? `-table-${tableNumber}` : ''}.png`;
      saveAs(qrUrl, fileName);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading restaurant data...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Page header with back button */}
        <div className={styles.pageHeader}>
          <h1>{restaurant?.name || 'QR Code Generator'}</h1>
          <button 
            onClick={() => navigate('/dashboard')}
            className={styles.backButton}
          >
            <FiArrowLeft size={16} /> Back to Dashboard
          </button>
        </div>
        
        <div className={styles.qrFormSection}>
          <h2>Generate Menu QR Codes</h2>
          <p>Create custom QR codes for tables or specific locations.</p>
          
          <div className={styles.formGroup}>
            <label htmlFor="tableNumber">Table or Location Number (optional)</label>
            <input
              id="tableNumber"
              type="text"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="e.g. 12, Room 301"
              className={styles.input}
            />
            <p className={styles.helpText}>Leave blank to create a general menu QR code</p>
          </div>
          
          {/* Add color customization options */}
          <div className={styles.colorOptions}>
            <div className={styles.formGroup}>
              <label htmlFor="fgColor">QR Code Color</label>
              <div className={styles.colorPickerGroup}>
                <input
                  id="fgColor"
                  type="color"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                  className={styles.colorPicker}
                />
                <span className={styles.colorValue}>{fgColor}</span>
              </div>
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="bgColor">Background Color</label>
              <div className={styles.colorPickerGroup}>
                <input
                  id="bgColor"
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className={styles.colorPicker}
                />
                <span className={styles.colorValue}>{bgColor}</span>
              </div>
            </div>
          </div>
        </div>
        
        {qrUrl && (
          <div className={styles.qrCodeSection}>
            <div className={styles.qrCodeWrapper} style={{ backgroundColor: bgColor }}>
              <img src={qrUrl} alt="Menu QR Code" className={styles.qrCode} />
              <div className={styles.qrInfo}>
                <p><strong>Restaurant:</strong> {restaurant?.name}</p>
                {tableNumber && <p><strong>Table/Location:</strong> {tableNumber}</p>}
              </div>
            </div>
            
            <button onClick={downloadQRCode} className={styles.downloadButton}>
              <FiDownload size={18} /> Download QR Code
            </button>
            
            <div className={styles.instructions}>
              <h3>How to use</h3>
              <ol>
                <li>Download this QR code</li>
                <li>Print and place it on your restaurant tables</li>
                <li>Customers can scan it to access your digital menu</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}